import { createSelector } from "reselect";
import * as R from "ramda";
import { getters } from "Modules/AddEditColumnModal/model";
import * as STANDARD_FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";

export const getIsPhoneDisabled = createSelector(getters.field, field => {
  const phoneDisplayType = R.pathOr(
    "",
    ["settings", "phoneDisplayType"],
    field
  );

  return phoneDisplayType === "international";
});

export const getEditField = createSelector(
  (state, props) => getters.fields(state, props),
  (_, props) => props.fieldId,
  (moduleFields, fieldId) => {
    const moduleField =
      R.find(
        field => field.id === fieldId || field.internal_name === fieldId,
        moduleFields
      ) || {};
    const fieldType = R.path(["settings", "isReferenceField"], moduleField)
      ? "reference"
      : R.path(["type"], moduleField);

    return {
      ...moduleField,
      type: fieldType
    };
  }
);

export const getIsStandardField = createSelector(
  (state, props) => getters.fields(state, props),
  (_, props) => props.fieldId,
  getEditField,
  (moduleFields, fieldId, field) => {
    if (R.isNil(fieldId)) {
      return false;
    }

    return R.propEq("source", "standard", field);
  }
);

export const getErrorMessage = createSelector(
  getters.name,
  getters.type,
  getters.lookupSettings,
  getters.referenceSettings,
  (name, type, lookupSettings, referenceSettings) => {
    if (name.length === 0) {
      return "Please enter the column's name";
    } else if (type === "lookup" && !lookupSettings.moduleId) {
      return "Select a module";
    } else if (
      type === "reference" &&
      (!referenceSettings.lookupFieldId || !referenceSettings.referenceFieldId)
    ) {
      return "Select an existing lookup field";
    }

    return null;
  }
);

export const getMaxCharacterMessage = createSelector(
  getters.type,
  getters.maxLength,
  (type, maxLength) => {
    if (type === "text" || type === "email") {
      return {
        message: "Default character limit: 255",
        error: maxLength > 255
      };
    }
    if (type === "textarea") {
      return {
        message: "Default character limit: 5000",
        error: maxLength > 5000
      };
    }

    return null;
  }
);

export const getFileOptions = R.always([
  { value: "pdf", label: "PDF" },
  { value: "ppt", label: "PPT" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
  { value: "word", label: "Word" },
  { value: "excel", label: "Excel" },
  { value: "zip", label: "ZIP" },
  { value: "png", label: "PNG" },
  { value: "jpeg", label: "JPEG" },
  { value: "gif", label: "GIF" },
  { value: "svg", label: "SVG" },
  { value: "mp4", label: "MP4" }
]);

export const getSelectedFileOptions = createSelector(
  getters.fileSettings,
  R.propOr([], "allowedFileTypes")
);

export const getIsAllowMultiUpload = createSelector(
  getters.fileSettings,
  R.propOr([], "allowMultiple")
);

export const getShowMaxLength = createSelector(getters.type, type =>
  ["text", "textarea", "wysiwyg", "email"].includes(type)
);

export const getShowLanguageVariants = createSelector(getters.type, type =>
  ["text", "textarea", "wysiwyg"].includes(type)
);

export const getMaxLengthFormatted = createSelector(
  getters.maxLength,
  maxLength => (R.isEmpty(maxLength) ? 0 : parseInt(maxLength, 10))
);

export const getIsValid = createSelector(
  getters.name,
  getters.type,
  getMaxLengthFormatted,
  (name, type, maxLength) => {
    if (!name || !name.length || name.length > 255) {
      return false;
    }

    if (maxLength < 0) {
      return false;
    }

    if (["text", "email"].includes(type) && maxLength > 255) {
      return false;
    }

    return !(["textarea"].includes(type) && maxLength > 5000);
  }
);

export const getCanStandardFieldBeEdited = createSelector(
  getters.fieldId,
  fieldId => {
    return [
      // Contacts
      STANDARD_FIELD_IDS.CONTACTS.TITLE,
      STANDARD_FIELD_IDS.CONTACTS.ABOUT,
      STANDARD_FIELD_IDS.CONTACTS.PREFERRED_SESSION_FORMAT,
      STANDARD_FIELD_IDS.CONTACTS.HEADCOUNT,
      STANDARD_FIELD_IDS.CONTACTS.ORGANIZATION_STRUCTURE,
      STANDARD_FIELD_IDS.CONTACTS.INDUSTRY,
      STANDARD_FIELD_IDS.CONTACTS.GLOBAL_REGION,
      STANDARD_FIELD_IDS.CONTACTS.AUDIENCE_TYPE,
      STANDARD_FIELD_IDS.CONTACTS.TARGET_AGE_RANGE,
      STANDARD_FIELD_IDS.CONTACTS.TOPIC_EXPERTISE,
      STANDARD_FIELD_IDS.CONTACTS.BRAND,
      // Sessions
      STANDARD_FIELD_IDS.SESSIONS.TITLE,
      STANDARD_FIELD_IDS.SESSIONS.DESCRIPTION,
      STANDARD_FIELD_IDS.SESSIONS.CEU_CREDITS
    ].includes(fieldId);
  }
);

export const getLanguageProperties = createSelector(
  getters.fieldLanguages,
  getters.enabledLanguages,
  (fieldLanguages, enabledLanguages) => {
    return {
      // @NOTE: Default lanuage is being filtered out of field.lanuages in the API
      default: {
        code: "en_US",
        label: "English (US)",
        value: ""
      },
      variants: enabledLanguages.map(l => {
        const language = fieldLanguages.find(fl => fl.language === l.locale);
        return {
          code: l.locale,
          label: l.name,
          show: language ? language.enabled : false,
          value: language && language.name ? language.name : ""
        };
      })
    };
  }
);
